import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { providers, admin, media } from '../services/api';
import ProfileMediaEditor from './ProfileMediaEditor';
import EnquiryForm from './EnquiryForm';
import Navigation from './Navigation';
import Masonry from 'react-masonry-css';
import Lightbox from 'yet-another-react-lightbox';
import Video from 'yet-another-react-lightbox/plugins/video';
import 'yet-another-react-lightbox/styles.css';
import {
  ArrowLeft, MapPin, Phone, Mail, Globe, MessageSquare, Users, Calendar,
  Award, Shield, DollarSign, Clock, Languages, CheckCircle, Star,
  Facebook, Instagram, Youtube, Linkedin, Edit, Building, User, CreditCard,
  FileText, AlertCircle, Briefcase, TrendingUp, Send, Heart
} from 'lucide-react';
import useShortlist from '../hooks/useShortlist';

function ModernProviderDetail() {
  const { id } = useParams();
  const { isShortlisted, toggle } = useShortlist();
  const navigate = useNavigate();
  const [provider, setProvider] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [editorOpen, setEditorOpen] = useState(false);
  const [editType, setEditType] = useState(null);
  const [isOwner, setIsOwner] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);
  const [canEdit, setCanEdit] = useState(false);
  const [enquiryFormOpen, setEnquiryFormOpen] = useState(false);
  const [isImpersonating, setIsImpersonating] = useState(false);
  const [publishing, setPublishing] = useState(false);
  const [publishMessage, setPublishMessage] = useState('');
  const [activeTab, setActiveTab] = useState('overview');
  const [providerMedia, setProviderMedia] = useState(null);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxIndex, setLightboxIndex] = useState(0);
  const [bannerError, setBannerError] = useState(false);

  // Helper function to get full image URL
  const getImageUrl = (relativeUrl) => {
    if (!relativeUrl) return null;
    if (relativeUrl.startsWith('http')) return relativeUrl;
    return `https://api.bridalconnect.co.za${relativeUrl}`;
  };

  useEffect(() => {
    loadProviderDetails();
    loadProviderMedia();
  }, [id]);

  // Set SEO page title and meta description
  useEffect(() => {
    if (provider) {
      const category = provider.category_name || '';
      const city = provider.city || '';
      const title = category && city
        ? `${provider.business_name} - ${category} in ${city} | BridalConnect`
        : `${provider.business_name} | BridalConnect`;
      document.title = title;

      let metaDesc = document.querySelector('meta[name="description"]');
      if (!metaDesc) {
        metaDesc = document.createElement('meta');
        metaDesc.name = 'description';
        document.head.appendChild(metaDesc);
      }
      metaDesc.content = provider.description
        ? provider.description.substring(0, 160)
        : `${provider.business_name} - ${category} wedding service provider in ${city}, KwaZulu-Natal`;
    }
    return () => { document.title = 'BridalConnect - Wedding Service Providers'; };
  }, [provider]);

  const loadProviderDetails = async () => {
    try {
      setLoading(true);
      const response = await providers.getById(id);
      setProvider(response.data);

      // Check if current user is the owner or admin (using API response)
      const currentUser = JSON.parse(localStorage.getItem('user') || '{}');
      const token = localStorage.getItem('token');
      const impersonating = localStorage.getItem('impersonating') === 'true';
      const owner = response.data.is_owner || currentUser.id === response.data.id;
      const admin = currentUser.email === 'steven@theweddingdj.co.za';

      setIsOwner(owner);
      setIsAdmin(admin);
      setIsImpersonating(impersonating);
      // Can only edit if you're the owner (including when admin is impersonating that user)
      setCanEdit(owner);
    } catch (error) {
      console.error('Failed to load provider details:', error);
      setError('Failed to load provider details');
    } finally {
      setLoading(false);
    }
  };


  const loadProviderMedia = async () => {
    try {
      const response = await media.getProviderMedia(id);
      setProviderMedia(response.data);
    } catch (error) {
      console.error('Failed to load provider media:', error);
    }
  };

  const handleImpersonate = async () => {
    if (!provider) return;

    try {
      // Call backend to get real JWT token for the provider
      const response = await admin.impersonate(provider.id);

      // Store original admin session
      const originalUser = localStorage.getItem('user');
      const originalToken = localStorage.getItem('token');
      localStorage.setItem('impersonate_original_user', originalUser);
      localStorage.setItem('impersonate_original_token', originalToken);

      // Replace with provider's real token and data
      localStorage.setItem('token', response.data.token);
      localStorage.setItem('user', JSON.stringify(response.data.provider));
      localStorage.setItem('impersonating', 'true');
      localStorage.setItem('impersonating_type', 'provider');

      // Reload current page to apply new session
      window.location.reload();
    } catch (error) {
      console.error('Failed to impersonate provider:', error);
      alert('Failed to impersonate provider. Please try again.');
    }
  };

  const handlePublishProfile = async () => {
    if (!window.confirm('Ready to publish your profile? It will be visible to couples searching for vendors.')) {
      return;
    }

    try {
      setPublishing(true);
      const response = await providers.publishProfile();
      setPublishMessage(response.data.message || 'Profile published successfully!');

      // Reload provider details to update is_published status
      await loadProviderDetails();

      // Clear message after 5 seconds
      setTimeout(() => setPublishMessage(''), 5000);
    } catch (error) {
      console.error('Failed to publish profile:', error);
      setPublishMessage('Failed to publish profile. Please try again.');
      setTimeout(() => setPublishMessage(''), 5000);
    } finally {
      setPublishing(false);
    }
  };

  const handleEditMedia = (type) => {
    setEditType(type);
    setEditorOpen(true);
  };

  const handleMediaSaved = () => {
    loadProviderDetails();
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-100 via-pink-50 to-purple-100 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-t-4 border-purple-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading provider details...</p>
        </div>
      </div>
    );
  }

  if (error || !provider) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-100 via-pink-50 to-purple-100 flex items-center justify-center">
        <div className="text-center">
          <AlertCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
          <p className="text-red-600 text-xl">{error || 'Provider not found'}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-100 via-pink-50 to-purple-100">
      <Navigation />
      {/* Header with Banner */}
      <div className="relative">
        {/* Banner */}
        <div className="relative h-64 md:h-80 lg:h-96 overflow-hidden bg-gradient-to-r from-purple-600 to-pink-600">
          {provider.banner_url && !bannerError ? (
            provider.banner_url.endsWith('.mp4') || provider.banner_url.endsWith('.webm') ? (
              <video
                src={getImageUrl(provider.banner_url)}
                className="w-full h-full object-cover"
                autoPlay
                loop
                muted
                playsInline
              />
            ) : (
              <img
                src={getImageUrl(provider.banner_url)}
                alt={`${provider.business_name} - ${provider.category_name || 'Wedding Service Provider'} in ${provider.city || 'KwaZulu-Natal'}`}
                className="w-full h-full object-cover"
                onError={() => setBannerError(true)}
              />
            )
          ) : (
            <div className="w-full h-full bg-gradient-to-r from-purple-600 to-pink-600 flex items-center justify-center">
              <div className="text-center text-white">
                <Building className="w-20 h-20 mx-auto mb-4 opacity-50" />
                {canEdit && (
                  <>
                    <p className="text-lg font-medium">Add a banner to showcase your business</p>
                    <p className="text-sm opacity-80 mt-1">Recommended: 1920x350px</p>
                  </>
                )}
              </div>
            </div>
          )}

          {/* Back Button */}
          <button
            onClick={() => navigate('/providers')}
            className="absolute top-4 left-4 bg-white/90 backdrop-blur-sm text-gray-700 px-4 py-2 rounded-lg hover:bg-white transition-all flex items-center gap-2 shadow-lg"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Directory
          </button>

          {/* Impersonate Button - only for admin when not impersonating and not viewing own profile */}
          {isAdmin && !isImpersonating && !isOwner && (
            <button
              onClick={handleImpersonate}
              className="absolute top-4 left-48 bg-purple-600 text-white px-4 py-2 rounded-lg hover:bg-purple-700 transition-all flex items-center gap-2 shadow-lg"
            >
              <User className="w-4 h-4" />
              Impersonate
            </button>
          )}

          {/* Unpublished Profile Notice */}
          {isOwner && !provider.is_published && (
            <div className="absolute top-16 left-4 right-4 md:left-auto md:right-4 md:max-w-md bg-yellow-500 text-white px-4 py-3 rounded-lg shadow-lg">
              <div className="flex items-start gap-2">
                <AlertCircle className="w-5 h-5 flex-shrink-0 mt-0.5" />
                <div className="flex-1">
                  <p className="font-bold text-sm">Profile Not Published</p>
                  <p className="text-xs mt-1">Your profile is in preview mode. Publish it to appear in search results.</p>
                </div>
              </div>
            </div>
          )}

          {/* Edit Banner Button */}
          {canEdit && (
            <button
              onClick={() => handleEditMedia('banner')}
              className="absolute top-4 right-20 bg-white/90 backdrop-blur-sm text-gray-700 px-4 py-2 rounded-lg hover:bg-white transition-all flex items-center gap-2 shadow-lg"
            >
              <Edit className="w-4 h-4" />
              {provider.banner_url ? 'Edit Banner' : 'Add Banner'}
            </button>
          )}

          {/* Publish Profile Button */}
          {isOwner && !provider.is_published && (
            <button
              onClick={handlePublishProfile}
              disabled={publishing}
              className="absolute top-4 right-4 md:right-48 bg-gradient-to-r from-green-500 to-green-600 text-white px-6 py-2 rounded-lg hover:from-green-600 hover:to-green-700 transition-all flex items-center gap-2 shadow-lg font-bold disabled:opacity-50"
            >
              <CheckCircle className="w-4 h-4" />
              {publishing ? 'Publishing...' : 'Publish Profile'}
            </button>
          )}
        </div>

        {/* Profile Header */}
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="relative -mt-20 md:-mt-24">
            <div className="bg-white rounded-2xl shadow-xl p-6 md:p-8">
              <div className="flex flex-col md:flex-row items-center md:items-start gap-6">
                {/* Logo */}
                <div className="relative">
                  <div className="w-32 h-32 md:w-40 md:h-40 rounded-xl overflow-hidden bg-gray-100 border-4 border-white shadow-lg">
                    {provider.logo_url ? (
                      <img
                        src={getImageUrl(provider.logo_url)}
                        alt={provider.business_name}
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-purple-100 to-pink-100">
                        <Building className="w-16 h-16 text-purple-600 opacity-50" />
                      </div>
                    )}
                  </div>

                  {canEdit && (
                    <button
                      onClick={() => handleEditMedia('logo')}
                      className="absolute bottom-2 right-2 w-10 h-10 bg-purple-600 text-white rounded-full flex items-center justify-center hover:bg-purple-700 transition-colors shadow-lg"
                    >
                      <Edit className="w-5 h-5" />
                    </button>
                  )}
                </div>

                {/* Business Info */}
                <div className="flex-1 text-center md:text-left">
                  <h1 className="text-3xl md:text-4xl font-bold text-gray-900 mb-3">
                    {provider.business_name}
                  </h1>

                  <div className="flex flex-wrap gap-2 justify-center md:justify-start mb-4">
                    {provider.categories && provider.categories.length > 0 && (() => {
                      // Map categories to display names (show subcategory if selected, otherwise category name)
                      const categoriesToDisplay = provider.categories.map(cat => ({
                        ...cat,
                        displayName: cat.selected_subcategory || cat.name
                      }));

                      // Filter out parent categories if their subcategories are also selected as separate categories
                      const filteredCategories = categoriesToDisplay.filter(cat => {
                        // Don't filter if this category has a selected subcategory (it's already specific)
                        if (cat.selected_subcategory) return true;

                        // Check if any other selected category has this category's name as a subcategory
                        const isParentOfAnotherSelected = provider.categories.some(otherCat =>
                          otherCat.id !== cat.id &&
                          otherCat.subcategories &&
                          otherCat.subcategories.some(subcat =>
                            subcat.toLowerCase() === cat.name.toLowerCase()
                          )
                        );
                        return !isParentOfAnotherSelected;
                      });

                      return filteredCategories.map((cat, idx) => (
                        <span key={idx} className="inline-flex items-center gap-1 px-3 py-1 bg-blue-100 text-blue-700 rounded-full text-sm font-medium">
                          <Briefcase className="w-4 h-4" />
                          {cat.displayName}
                        </span>
                      ));
                    })()}
                    {/* Fallback to category_name if categories array not available */}
                    {(!provider.categories || provider.categories.length === 0) && provider.category_name && (
                      <span className="inline-flex items-center gap-1 px-3 py-1 bg-blue-100 text-blue-700 rounded-full text-sm font-medium">
                        <Briefcase className="w-4 h-4" />
                        {provider.category_name}
                      </span>
                    )}
                  </div>

                  {(provider.city || provider.province) && (
                    <p className="text-gray-600 flex items-center gap-2 justify-center md:justify-start">
                      <MapPin className="w-4 h-4" />
                      {[provider.city, provider.province].filter(Boolean).join(', ')}
                    </p>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Tab Navigation */}
        <div className="mb-6">
          <div className="border-b border-gray-200">
            <nav className="-mb-px flex space-x-8">
              <button
                onClick={() => setActiveTab('overview')}
                className={`${
                  activeTab === 'overview'
                    ? 'border-purple-600 text-purple-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                } whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm transition-colors`}
              >
                Overview
              </button>
              {providerMedia?.gallery?.filter(m => m.media_type === 'image').length > 0 && (
                <button
                  onClick={() => setActiveTab('gallery')}
                  className={`${
                    activeTab === 'gallery'
                      ? 'border-purple-600 text-purple-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  } whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm transition-colors`}
                >
                  Gallery ({providerMedia.gallery.filter(m => m.media_type === 'image').length})
                </button>
              )}
              {providerMedia?.gallery?.filter(m => m.media_type === 'youtube').length > 0 && (
                <button
                  onClick={() => setActiveTab('videos')}
                  className={`${
                    activeTab === 'videos'
                      ? 'border-purple-600 text-purple-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  } whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm transition-colors`}
                >
                  Videos ({providerMedia.gallery.filter(m => m.media_type === 'youtube').length})
                </button>
              )}
            </nav>
          </div>
        </div>

        {/* Tab Content */}
        {activeTab === 'overview' && (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Left Column - Main Information */}
            <div className="lg:col-span-2 space-y-6">
              {/* Profile Completion Prompt for Owners */}
            {canEdit && (!provider.description || !provider.established_year || !provider.services_offered?.length) && (
              <div className="bg-gradient-to-r from-purple-50 to-pink-50 border-2 border-purple-200 rounded-xl p-6">
                <h3 className="text-lg font-semibold text-purple-900 mb-2 flex items-center gap-2">
                  <AlertCircle className="w-5 h-5 text-purple-600" />
                  Complete Your Profile
                </h3>
                <p className="text-purple-700 mb-4">
                  Add more information to help potential clients learn about your business!
                </p>
                <button
                  onClick={() => navigate('/dashboard')}
                  className="bg-gradient-to-r from-purple-600 to-pink-600 text-white px-4 py-2 rounded-lg hover:from-purple-700 hover:to-pink-700 transition-all"
                >
                  Complete Profile →
                </button>
              </div>
            )}

            {/* About Section */}
            {provider.description ? (
              <div className="bg-white rounded-xl shadow-lg p-6">
                <h2 className="text-xl font-semibold text-gray-900 mb-4 flex items-center gap-2">
                  <FileText className="w-5 h-5 text-purple-600" />
                  About Us
                </h2>
                <p className="text-gray-700 leading-relaxed whitespace-pre-wrap">{provider.description}</p>
              </div>
            ) : !isOwner && (
              <div className="bg-white rounded-xl shadow-lg p-6">
                <h2 className="text-xl font-semibold text-gray-900 mb-4 flex items-center gap-2">
                  <FileText className="w-5 h-5 text-purple-600" />
                  About {provider.business_name}
                </h2>
                <p className="text-gray-500 italic">No description available yet.</p>
              </div>
            )}

            {/* Business Information */}
            {(provider.established_year || provider.years_experience || provider.team_size ||
              provider.events_completed || provider.business_registration ||
              provider.languages_spoken?.length > 0 || provider.has_insurance) && (
              <div className="bg-white rounded-xl shadow-lg p-6">
                <h2 className="text-xl font-semibold text-gray-900 mb-4 flex items-center gap-2">
                  <Building className="w-5 h-5 text-purple-600" />
                  Business Information
                </h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {provider.established_year && (
                    <div className="flex items-start gap-3">
                      <Calendar className="w-5 h-5 text-purple-600 mt-0.5" />
                      <div>
                        <p className="text-sm text-gray-500">Established</p>
                        <p className="text-gray-900 font-medium">{provider.established_year}</p>
                      </div>
                    </div>
                  )}
                  {provider.years_experience && (
                    <div className="flex items-start gap-3">
                      <TrendingUp className="w-5 h-5 text-purple-600 mt-0.5" />
                      <div>
                        <p className="text-sm text-gray-500">Experience</p>
                        <p className="text-gray-900 font-medium">{provider.years_experience} years</p>
                      </div>
                    </div>
                  )}
                  {provider.team_size && (
                    <div className="flex items-start gap-3">
                      <Users className="w-5 h-5 text-purple-600 mt-0.5" />
                      <div>
                        <p className="text-sm text-gray-500">Team Size</p>
                        <p className="text-gray-900 font-medium">{provider.team_size}</p>
                      </div>
                    </div>
                  )}
                  {provider.events_completed && (
                    <div className="flex items-start gap-3">
                      <Award className="w-5 h-5 text-purple-600 mt-0.5" />
                      <div>
                        <p className="text-sm text-gray-500">Events Completed</p>
                        <p className="text-gray-900 font-medium">{provider.events_completed}+</p>
                      </div>
                    </div>
                  )}
                  {provider.business_registration && (
                    <div className="flex items-start gap-3">
                      <FileText className="w-5 h-5 text-purple-600 mt-0.5" />
                      <div>
                        <p className="text-sm text-gray-500">Registration</p>
                        <p className="text-gray-900 font-medium">{provider.business_registration}</p>
                      </div>
                    </div>
                  )}
                  {provider.languages_spoken && provider.languages_spoken.length > 0 && (
                    <div className="flex items-start gap-3">
                      <Languages className="w-5 h-5 text-purple-600 mt-0.5" />
                      <div>
                        <p className="text-sm text-gray-500">Languages</p>
                        <p className="text-gray-900 font-medium">{provider.languages_spoken.join(', ')}</p>
                      </div>
                    </div>
                  )}
                  {provider.has_insurance && (
                    <div className="flex items-start gap-3 md:col-span-2">
                      <Shield className="w-5 h-5 text-green-600 mt-0.5" />
                      <div>
                        <p className="text-sm text-gray-500">Insurance</p>
                        <p className="text-gray-900 font-medium">
                          Fully Insured
                          {provider.insurance_details && ` - ${provider.insurance_details}`}
                        </p>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Services & Specialties */}
            {(provider.services_offered?.length > 0 || provider.specialties?.length > 0) && (
              <div className="bg-white rounded-xl shadow-lg p-6">
                <h2 className="text-xl font-semibold text-gray-900 mb-4 flex items-center gap-2">
                  <Briefcase className="w-5 h-5 text-purple-600" />
                  Services & Specialties
                </h2>

                {provider.services_offered && provider.services_offered.length > 0 && (
                  <div className="mb-6">
                    <h3 className="text-sm font-medium text-gray-700 mb-3">Services We Offer</h3>
                    <div className="flex flex-wrap gap-2">
                      {provider.services_offered.map((service, idx) => (
                        <span
                          key={idx}
                          className="px-3 py-1.5 bg-purple-100 text-purple-700 rounded-full text-sm"
                        >
                          {service}
                        </span>
                      ))}
                    </div>
                  </div>
                )}

                {provider.specialties && provider.specialties.length > 0 && (
                  <div>
                    <h3 className="text-sm font-medium text-gray-700 mb-3">Our Specialties</h3>
                    <div className="flex flex-wrap gap-2">
                      {provider.specialties.map((specialty, idx) => (
                        <span
                          key={idx}
                          className="px-3 py-1.5 bg-pink-100 text-pink-700 rounded-full text-sm"
                        >
                          {specialty}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Awards & Certifications */}
            {provider.awards_certifications?.length > 0 && (
              <div className="bg-white rounded-xl shadow-lg p-6">
                <h2 className="text-xl font-semibold text-gray-900 mb-4 flex items-center gap-2">
                  <Award className="w-5 h-5 text-purple-600" />
                  Awards & Certifications
                </h2>
                <div className="space-y-2">
                  {provider.awards_certifications.map((award, idx) => (
                    <div key={idx} className="flex items-center gap-2 text-gray-700">
                      <span className="text-yellow-500">🏆</span>
                      {award}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Pricing & Payment */}
            {(provider.price_range || provider.price_from || provider.payment_methods?.length > 0) && (
              <div className="bg-white rounded-xl shadow-lg p-6">
                <h2 className="text-xl font-semibold text-gray-900 mb-4 flex items-center gap-2">
                  <DollarSign className="w-5 h-5 text-purple-600" />
                  Pricing & Payment
                </h2>
                <div className="space-y-4">
                  {provider.price_range && (
                    <div className="flex items-center justify-between py-2 border-b border-gray-100">
                      <span className="text-gray-600">Price Range</span>
                      <span className="font-medium text-gray-900">{provider.price_range}</span>
                    </div>
                  )}
                  {provider.price_from && (
                    <div className="flex items-center justify-between py-2 border-b border-gray-100">
                      <span className="text-gray-600">Starting From</span>
                      <span className="font-medium text-gray-900">R{parseInt(provider.price_from).toLocaleString()}</span>
                    </div>
                  )}
                  {provider.price_to && (
                    <div className="flex items-center justify-between py-2 border-b border-gray-100">
                      <span className="text-gray-600">Up To</span>
                      <span className="font-medium text-gray-900">R{parseInt(provider.price_to).toLocaleString()}</span>
                    </div>
                  )}
                  {provider.deposit_required && (
                    <div className="flex items-center justify-between py-2 border-b border-gray-100">
                      <span className="text-gray-600">Deposit Required</span>
                      <span className="font-medium text-gray-900">
                        Yes {provider.deposit_percentage && `(${provider.deposit_percentage}%)`}
                      </span>
                    </div>
                  )}
                  {provider.payment_methods && provider.payment_methods.length > 0 && (
                    <div>
                      <p className="text-gray-600 mb-2">Accepted Payment Methods</p>
                      <div className="flex flex-wrap gap-2">
                        {provider.payment_methods.map((method, idx) => (
                          <span
                            key={idx}
                            className="inline-flex items-center gap-1 px-3 py-1 bg-gray-100 text-gray-700 rounded-full text-sm"
                          >
                            <CreditCard className="w-3 h-3" />
                            {method}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Booking Information */}
            {(provider.booking_lead_time || provider.cancellation_policy || provider.offers_consultation) && (
              <div className="bg-white rounded-xl shadow-lg p-6">
                <h2 className="text-xl font-semibold text-gray-900 mb-4 flex items-center gap-2">
                  <Clock className="w-5 h-5 text-purple-600" />
                  Booking Information
                </h2>
                <div className="space-y-4">
                  {provider.booking_lead_time && (
                    <div>
                      <p className="text-sm text-gray-600">Booking Lead Time</p>
                      <p className="text-gray-900 font-medium">{provider.booking_lead_time}</p>
                    </div>
                  )}
                  {provider.offers_consultation && (
                    <div>
                      <p className="text-sm text-gray-600">Consultation</p>
                      <p className="text-gray-900 font-medium">
                        Available {provider.consultation_fee ? `(R${provider.consultation_fee})` : '(Free)'}
                      </p>
                    </div>
                  )}
                  {provider.cancellation_policy && (
                    <div>
                      <p className="text-sm text-gray-600 mb-2">Cancellation Policy</p>
                      <p className="text-gray-700 whitespace-pre-wrap">{provider.cancellation_policy}</p>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>

          {/* Right Column - Contact & Social */}
          <div className="space-y-6">
            {/* Contact Card */}
            <div className="bg-white rounded-xl shadow-lg p-6 sticky top-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                <Phone className="w-5 h-5 text-purple-600" />
                Get in Touch
              </h3>

              <div className="space-y-4">
                {provider.contact_person && (
                  <div>
                    <p className="text-sm text-gray-600">Contact Person</p>
                    <p className="text-gray-900 font-medium flex items-center gap-2">
                      <User className="w-4 h-4 text-gray-400" />
                      {provider.contact_person}
                    </p>
                  </div>
                )}

                {provider.phone && (
                  <div>
                    <p className="text-sm text-gray-600">Phone</p>
                    <a
                      href={`tel:${provider.phone}`}
                      className="text-purple-600 font-medium flex items-center gap-2 hover:text-purple-700"
                    >
                      <Phone className="w-4 h-4" />
                      {provider.phone}
                    </a>
                  </div>
                )}

                {provider.alternative_phone && (
                  <div>
                    <p className="text-sm text-gray-600">Alternative Phone</p>
                    <a
                      href={`tel:${provider.alternative_phone}`}
                      className="text-purple-600 font-medium flex items-center gap-2 hover:text-purple-700"
                    >
                      <Phone className="w-4 h-4" />
                      {provider.alternative_phone}
                    </a>
                  </div>
                )}

                {provider.whatsapp_number && (
                  <div>
                    <p className="text-sm text-gray-600">WhatsApp</p>
                    <a
                      href={`https://wa.me/${provider.whatsapp_number.replace(/[^0-9]/g, '')}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-green-600 font-medium flex items-center gap-2 hover:text-green-700"
                    >
                      <MessageSquare className="w-4 h-4" />
                      {provider.whatsapp_number}
                    </a>
                  </div>
                )}

                {provider.email_public && (
                  <div>
                    <p className="text-sm text-gray-600">Email</p>
                    <a
                      href={`mailto:${provider.email_public}`}
                      className="text-purple-600 font-medium flex items-center gap-2 hover:text-purple-700"
                    >
                      <Mail className="w-4 h-4" />
                      {provider.email_public}
                    </a>
                  </div>
                )}

                {provider.website && (
                  <a
                    href={provider.website}
                    target="_blank"
                    rel="noopener noreferrer"
                    title={provider.category_name && provider.city
                      ? `${provider.category_name} in ${provider.city} - ${provider.business_name}`
                      : `${provider.business_name} Website`}
                    aria-label={provider.category_name && provider.city
                      ? `Visit ${provider.business_name} - ${provider.category_name} in ${provider.city}`
                      : `Visit ${provider.business_name} Website`}
                    className="w-full mt-4 bg-gradient-to-r from-purple-600 to-pink-600 text-white py-3 px-4 rounded-lg font-medium hover:from-purple-700 hover:to-pink-700 transition-all flex items-center justify-center gap-2"
                  >
                    <Globe className="w-4 h-4" />
                    Visit Website
                  </a>
                )}

                {provider.whatsapp_number && (
                  <a
                    href={`https://wa.me/${provider.whatsapp_number.replace(/[^0-9]/g, '')}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="w-full bg-green-600 text-white py-3 px-4 rounded-lg font-medium hover:bg-green-700 transition-colors flex items-center justify-center gap-2"
                  >
                    <MessageSquare className="w-4 h-4" />
                    WhatsApp Message
                  </a>
                )}

                {/* Enquiry Button for non-owners */}
                {!isOwner && (
                  <button
                    onClick={() => setEnquiryFormOpen(true)}
                    className="w-full bg-gradient-to-r from-purple-600 to-pink-600 text-white py-3 px-4 rounded-lg font-medium hover:shadow-lg transition-all flex items-center justify-center gap-2"
                  >
                    <Send className="w-4 h-4" />
                    Send Enquiry
                  </button>
                )}

                {/* Shortlist Button */}
                <button
                  onClick={() => toggle(provider.id)}
                  className={`w-full py-3 px-4 rounded-lg font-bold transition-all flex items-center justify-center gap-2 text-lg ${
                    isShortlisted(provider.id)
                      ? 'bg-pink-50 text-pink-600 border-2 border-pink-300 hover:bg-pink-100'
                      : 'bg-gray-50 text-gray-600 border-2 border-gray-200 hover:bg-gray-100'
                  }`}
                >
                  <Heart className={`w-5 h-5 ${isShortlisted(provider.id) ? 'fill-pink-500' : ''}`} />
                  {isShortlisted(provider.id) ? 'Shortlisted' : 'Add to Shortlist'}
                </button>

                {/* Claim Listing Button - for unclaimed providers */}
                {!provider.is_claimed && (
                  <button
                    onClick={() => navigate(`/claim/${provider.id}`)}
                    className="w-full bg-gradient-to-r from-green-500 to-emerald-600 text-white py-3 px-4 rounded-lg font-bold hover:from-green-600 hover:to-emerald-700 hover:shadow-lg transition-all flex items-center justify-center gap-2 text-lg"
                  >
                    <CheckCircle className="w-5 h-5" />
                    Claim this listing
                  </button>
                )}

                {publishMessage && (
                  <div className={`p-3 rounded-lg text-sm font-semibold ${
                    publishMessage.includes('success')
                      ? 'bg-green-100 text-green-800'
                      : 'bg-red-100 text-red-800'
                  }`}>
                    {publishMessage}
                  </div>
                )}
              </div>
            </div>

            {/* Location Card */}
            {(provider.city || provider.province) && (
              <div className="bg-white rounded-xl shadow-lg p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                  <MapPin className="w-5 h-5 text-purple-600" />
                  Service Area
                </h3>
                <p className="text-gray-700">
                  {[provider.city, provider.province, provider.postal_code].filter(Boolean).join(', ')}
                </p>
                {provider.willing_to_travel && (
                  <div className="mt-3 flex items-center gap-2 text-green-600">
                    <CheckCircle className="w-4 h-4" />
                    <span>Available for travel</span>
                    {provider.radius_km && (
                      <span className="text-gray-600">({provider.radius_km}km radius)</span>
                    )}
                  </div>
                )}
              </div>
            )}

            {/* Social Media */}
            {(provider.instagram_url || provider.facebook_review_url || provider.youtube_url ||
              provider.linkedin_url || provider.google_review_url || provider.tiktok_url || provider.pinterest_url) && (
              <div className="bg-white rounded-xl shadow-lg p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Follow Us</h3>
                <div className="grid grid-cols-2 gap-3">
                  {provider.facebook_review_url && (
                    <a
                      href={provider.facebook_review_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="bg-blue-600 text-white py-2 px-4 rounded-lg text-sm font-medium hover:bg-blue-700 transition-colors flex items-center justify-center gap-2"
                    >
                      <Facebook className="w-4 h-4" />
                      Facebook{provider.facebook_review_count > 0 && ` (${provider.facebook_review_count})`}
                    </a>
                  )}
                  {provider.instagram_url && (
                    <a
                      href={provider.instagram_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="bg-gradient-to-r from-purple-600 to-pink-600 text-white py-2 px-4 rounded-lg text-sm font-medium hover:from-purple-700 hover:to-pink-700 transition-all flex items-center justify-center gap-2"
                    >
                      <Instagram className="w-4 h-4" />
                      Instagram
                    </a>
                  )}
                  {provider.youtube_url && (
                    <a
                      href={provider.youtube_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="bg-red-600 text-white py-2 px-4 rounded-lg text-sm font-medium hover:bg-red-700 transition-colors flex items-center justify-center gap-2"
                    >
                      <Youtube className="w-4 h-4" />
                      YouTube
                    </a>
                  )}
                  {provider.linkedin_url && (
                    <a
                      href={provider.linkedin_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="bg-blue-700 text-white py-2 px-4 rounded-lg text-sm font-medium hover:bg-blue-800 transition-colors flex items-center justify-center gap-2"
                    >
                      <Linkedin className="w-4 h-4" />
                      LinkedIn
                    </a>
                  )}
                  {provider.google_review_url && (
                    <a
                      href={provider.google_review_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="bg-blue-500 text-white py-2 px-4 rounded-lg text-sm font-medium hover:bg-blue-600 transition-colors col-span-2 flex items-center justify-center gap-2"
                    >
                      <Star className="w-4 h-4" />
                      Google Reviews{provider.google_review_count > 0 && ` (${provider.google_review_count})`}
                    </a>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
        )}

        {/* Gallery Tab */}
        {activeTab === 'gallery' && providerMedia?.gallery && (
          <div className="w-full">
            <style>{`
              .masonry-grid {
                display: flex;
                margin-left: -15px;
                width: auto;
              }
              .masonry-grid_column {
                padding-left: 15px;
                background-clip: padding-box;
              }
              .masonry-grid_column > div {
                margin-bottom: 15px;
              }
            `}</style>
            <Masonry
              breakpointCols={{
                default: 4,
                1100: 3,
                700: 2,
                500: 1
              }}
              className="masonry-grid"
              columnClassName="masonry-grid_column"
            >
              {providerMedia.gallery
                .filter(item => item.media_type === 'image')
                .map((item, index) => (
                  <div key={item.id} className="relative group">
                    <img
                      src={`https://api.bridalconnect.co.za${item.url}`}
                      alt={item.caption || 'Gallery image'}
                      className="w-full rounded-lg shadow-lg hover:shadow-xl transition-shadow cursor-pointer"
                      onClick={() => {
                        setLightboxIndex(index);
                        setLightboxOpen(true);
                      }}
                    />
                    {item.caption && (
                      <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/70 to-transparent p-4 rounded-b-lg">
                        <p className="text-white text-sm">{item.caption}</p>
                      </div>
                    )}
                  </div>
                ))}
            </Masonry>
          </div>
        )}

        {/* Videos Tab */}
        {activeTab === 'videos' && providerMedia?.gallery && (
          <div className="w-full">
            <style>{`
              .masonry-grid {
                display: flex;
                margin-left: -15px;
                width: auto;
              }
              .masonry-grid_column {
                padding-left: 15px;
                background-clip: padding-box;
              }
              .masonry-grid_column > div {
                margin-bottom: 15px;
              }
            `}</style>
            <Masonry
              breakpointCols={{
                default: 3,
                1100: 2,
                700: 1
              }}
              className="masonry-grid"
              columnClassName="masonry-grid_column"
            >
              {providerMedia.gallery
                .filter(item => item.media_type === 'youtube')
                .map((item, index) => (
                  <div
                    key={item.id}
                    className="relative group bg-white rounded-lg shadow-lg overflow-hidden cursor-pointer hover:shadow-xl transition-shadow"
                    onClick={() => {
                      setLightboxIndex(index);
                      setLightboxOpen(true);
                    }}
                  >
                    <div className="relative" style={{ paddingBottom: '56.25%' }}>
                      <iframe
                        className="absolute top-0 left-0 w-full h-full pointer-events-none"
                        src={`https://www.youtube.com/embed/${new URL(item.url).searchParams.get('v')}`}
                        title={item.caption || 'YouTube video'}
                        frameBorder="0"
                        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                        allowFullScreen
                      />
                    </div>
                    <div className="absolute inset-0 flex items-center justify-center bg-black/10 group-hover:bg-black/20 transition-colors">
                      <div className="w-16 h-16 bg-white/90 rounded-full flex items-center justify-center">
                        <svg className="w-8 h-8 text-purple-600" fill="currentColor" viewBox="0 0 24 24">
                          <path d="M8 5v14l11-7z"/>
                        </svg>
                      </div>
                    </div>
                    {item.caption && (
                      <div className="p-4 bg-white">
                        <p className="text-gray-700 text-sm">🎥 {item.caption}</p>
                      </div>
                    )}
                  </div>
                ))}
            </Masonry>
          </div>
        )}
      </div>

      {/* Media Editor Modal */}
      <ProfileMediaEditor
        isOpen={editorOpen}
        onClose={() => setEditorOpen(false)}
        type={editType}
        currentImage={editType === 'logo' ? provider?.logo_url : provider?.banner_url}
        onSave={handleMediaSaved}
      />

      {/* Enquiry Form Modal */}
      {enquiryFormOpen && provider && (
        <EnquiryForm
          provider={provider}
          onClose={() => setEnquiryFormOpen(false)}
          onSuccess={(result) => {
            alert('Enquiry sent successfully! The provider will contact you soon.');
          }}
        />
      )}

      {/* Lightbox for Gallery and Videos */}
      {providerMedia?.gallery && activeTab === 'gallery' && (
        <Lightbox
          open={lightboxOpen}
          close={() => setLightboxOpen(false)}
          index={lightboxIndex}
          slides={providerMedia.gallery
            .filter(item => item.media_type === 'image')
            .map(item => ({
              src: `https://api.bridalconnect.co.za${item.url}`,
              alt: item.caption || 'Gallery image',
              title: item.caption
            }))}
        />
      )}

      {providerMedia?.gallery && activeTab === 'videos' && (
        <Lightbox
          open={lightboxOpen}
          close={() => setLightboxOpen(false)}
          index={lightboxIndex}
          plugins={[Video]}
          slides={providerMedia.gallery
            .filter(item => item.media_type === 'youtube')
            .map(item => ({
              type: 'video',
              sources: [{
                src: item.url,
                type: 'video/youtube'
              }],
              title: item.caption
            }))}
        />
      )}
    </div>
  );
}

export default ModernProviderDetail;